import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAsync, useLocalStorage } from 'react-use';
import { v4 as uuid } from 'uuid';
import { useSetAuthToken } from 'api/authTokenApi';
import { type ErrorResponse } from 'api/fetchAPI';
import { useSetSession } from 'context';
import { useLoginCountry, DEFAULT_COUNTRY } from 'hooks';
import { EidProviderName } from '../../types';
import {
  completeScriveEid,
  getAppCountries,
  ScriveProvider,
  SignInSuccessData,
  startScriveEid,
} from './api';

export interface InitEidArgs {
  provider: EidProviderName;
}

const SCRIVE_TRANSACTION_STORAGE_KEY = 'qb_web_scriveTransactionId';

const SCRIVE_SUPPORTED_PROVIDERS: readonly ScriveProvider[] = [
  'seBankID',
  'noBankID',
  'fiTupas',
  'dkMitID',
  'nlIDIN',
  'onfido',
];

const toScriveProvider = (
  provider: EidProviderName
): ScriveProvider | undefined =>
  SCRIVE_SUPPORTED_PROVIDERS.find((p) => p === provider);

export const useEidLogin = ({
  success,
  receiveOrderPreviewId,
}: {
  success: (data: SignInSuccessData) => void;
  userId?: string;
  receiveOrderPreviewId?: string;
}) => {
  const [deviceId] = useLocalStorage<string>('qb_web_deviceId', uuid());
  const { loginCountry, changeLoginCountry } = useLoginCountry();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);
  const setSession = useSetSession();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mutateAsync: setAuthTokenAsync } = useSetAuthToken();
  const [rememberMe, setRememberMe] = useState(true);
  const exchangeInProgress = useRef(false);

  const { value: appCountries, loading: loadingCountries } = useAsync(
    async () =>
      (await getAppCountries(location.pathname.includes('onramp'))).countries
  );

  const selectedCountry = useMemo(() => {
    if (!appCountries) {
      return undefined;
    }
    const foundCountry = appCountries.find(
      (country) => country.code === loginCountry
    );
    if (!foundCountry) {
      return appCountries.find((country) => country.code === DEFAULT_COUNTRY);
    }
    return foundCountry;
  }, [appCountries, loginCountry]);

  const handleSuccess = useCallback(
    async (data: SignInSuccessData) => {
      await setSession(
        {
          userId: data.userId,
          firstOnboardingCompleted: data.firstOnboardingCompleted,
          email: data.kyc.email?.address,
          fullName: data.kyc.firstName + ' ' + data.kyc.lastName,
        },
        data.tokenTtlInSeconds
      );
      await setAuthTokenAsync(data.token);
      success(data);
    },
    [setSession, success, setAuthTokenAsync]
  );

  useEffect(() => {
    const transactionId = searchParams.get('transaction_id');
    if (!transactionId || !selectedCountry || exchangeInProgress.current) {
      return;
    }

    // Session-fixation guard: only complete a transaction this browser
    // started. Without this check, an attacker could craft a callback
    // URL with their own transaction_id and authenticate the victim's
    // browser into the attacker's identity.
    const expectedTransactionId = sessionStorage.getItem(
      SCRIVE_TRANSACTION_STORAGE_KEY
    );
    if (!expectedTransactionId || expectedTransactionId !== transactionId) {
      sessionStorage.removeItem(SCRIVE_TRANSACTION_STORAGE_KEY);
      searchParams.delete('transaction_id');
      searchParams.delete('success');
      setSearchParams(searchParams, { replace: true });
      setError({
        errorCode: 'NOT_AVAILABLE',
        reason: 'Login session has expired. Please try again.',
      });
      return;
    }

    exchangeInProgress.current = true;
    sessionStorage.removeItem(SCRIVE_TRANSACTION_STORAGE_KEY);

    searchParams.delete('transaction_id');
    searchParams.delete('success');
    setSearchParams(searchParams, { replace: true });

    const exchange = async () => {
      setIsAuthenticating(true);
      setError(null);
      try {
        const data = await completeScriveEid({
          transactionId,
          deviceId: deviceId as string,
          rememberMe,
          nationality: selectedCountry.code,
          receiveOrderPreviewId,
        });
        await handleSuccess(data);
      } catch (err) {
        setError(err as ErrorResponse);
      } finally {
        setIsAuthenticating(false);
        exchangeInProgress.current = false;
      }
    };

    void exchange();
  }, [
    searchParams,
    setSearchParams,
    selectedCountry,
    deviceId,
    rememberMe,
    receiveOrderPreviewId,
    handleSuccess,
  ]);

  const start = useCallback(
    async (input?: InitEidArgs) => {
      if (!input?.provider || !selectedCountry || !deviceId) return;

      const scriveProvider = toScriveProvider(input.provider);
      if (!scriveProvider) {
        setError({
          errorCode: 'NOT_AVAILABLE',
          reason: `Provider ${input.provider} not supported`,
        });
        return;
      }

      setError(null);
      setIsAuthenticating(true);
      try {
        const { transactionId, accessUrl } = await startScriveEid({
          provider: scriveProvider,
          redirectUrl: window.location.origin + location.pathname,
          deviceId,
          rememberMe,
          nationality: selectedCountry.code,
          receiveOrderPreviewId,
        });
        // Bind the transaction to this browser session so the callback
        // handler can refuse a transaction_id we didn't start.
        sessionStorage.setItem(SCRIVE_TRANSACTION_STORAGE_KEY, transactionId);
        window.location.href = accessUrl;
      } catch (err) {
        setError(err as ErrorResponse);
        setIsAuthenticating(false);
      }
    },
    [
      selectedCountry,
      deviceId,
      rememberMe,
      receiveOrderPreviewId,
      location.pathname,
    ]
  );

  const cancel = useCallback(() => {
    setIsAuthenticating(false);
    setError(null);
  }, []);

  return {
    start,
    cancel,
    isPollingLogin: isAuthenticating,
    fetchingAuthData: false,
    pollingError: error,
    loadingCountries,
    appCountries,
    rememberMe,
    setRememberMe,
    changeLoginCountry,
    selectedCountry,
  };
};
